import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Member } from '../../types';

interface AuthState {
  user: Member | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Load user from localStorage if it exists
const loadUserFromStorage = (): Member | null => {
  try {
    const userJson = localStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    console.error('Error loading user from localStorage:', error);
    return null;
  }
};

const _storedUser = loadUserFromStorage();
const initialState: AuthState = {
  user: _storedUser,
  // Legacy localStorage token (transition only); new cookie-based sessions have no
  // token here — the real credential is the httpOnly cookie.
  token: localStorage.getItem('accessToken'),
  isAuthenticated: !!_storedUser,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ token?: string; user: Member }>) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token || null;
      state.user = action.payload.user;
      state.error = null;

      // H6: the access token now lives in an httpOnly cookie set by the backend —
      // do NOT persist it to localStorage (that was the XSS-exfil risk). Only the
      // (non-secret) user profile is cached, for a synchronous isAuthenticated at boot.
      // Also drop any LEGACY localStorage token from a pre-H6 session, so the request
      // interceptor never shadows the fresh cookie with a stale Bearer header.
      localStorage.removeItem('accessToken');
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    },
    setUser: (state, action: PayloadAction<Member>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUser,
  clearError,
  setLoading,
} = authSlice.actions;

export default authSlice.reducer;
